import React, { useCallback, useMemo, useContext } from 'react';
import { useParams } from 'react-router-dom';
import ItemContent, { IOnChangeInputs } from '../../../components/ItemContent/ItemContent';
import { IEditForm } from '../../../types/interfaces/IEditForm';
import { NSPackages } from '../../../types/interfaces/NSPackages';
import { EStatus } from '../../../types/enums/EStatus';
import { useTranslation } from 'react-i18next';
import tPaths from '../../../utils/tPaths';
import { NSSystemUsers } from '../../../types/interfaces/NSSystemUsers';
import usePermissionsStatus from '../../../utils/usePermissionsStatus';
import { EPaths } from '../../../types/enums/EPaths';
import { SessionContext } from '../../../contexts/session/SessionContext';

const EditSystemUser = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  const { permissions } = useContext(SessionContext);

  const { permissionsStatusItems, arrayToPermissions, permissionsToArray } = usePermissionsStatus();

  const dataMapper = useCallback(
    (data: NSSystemUsers.IDetails): IEditForm[][] => {
      console.log(data.permissions);
      return [
        [
          {
            type: 'input',
            fieldName: 'nameSurname',
            label: t(tPaths.editFormLabels.name),
            initialValue: data.nameSurname,
          },

          {
            type: 'input',
            fieldName: 'email',
            label: t(tPaths.editFormLabels.email),
            initialValue: data.email,
          },
        ],
        [
          {
            type: 'checkboxGrid',
            fieldName: 'permissions',
            label: t(tPaths.editFormLabels.modules),
            initialValue: arrayToPermissions(data.permissions),
            checkboxGrid: [
              { key: 'view', label: t(tPaths.systemUsers.view) },
              { key: 'edit', label: t(tPaths.systemUsers.edit) },
            ],
            items: permissionsStatusItems,
          },
        ],
      ];
    },
    [arrayToPermissions, permissionsStatusItems, t],
  );

  const updateDataFormatter = useCallback(
    (fields: any) => ({
      nameSurname: fields.nameSurname.value,
      email: fields.email.value,
      status: fields.status ? EStatus.Active : EStatus.Inactive,
      permissions: permissionsToArray(fields.permissions.value),
    }),
    [permissionsToArray],
  );

  const switches = useMemo(() => [{ key: 'status', label: t(tPaths.itemContent.active) }], [t]);
  const switchesDataMapper = useCallback((data: NSPackages.IPackageDetails) => {
    return { status: data.status.current === EStatus.Active };
  }, []);

  const handleOnChange = useCallback(({ fieldName, value }: IOnChangeInputs) => {
    if (fieldName === 'permissions') {
      return {
        carrierStatus: {
          view: value.carrierStatus.view,
          edit: value.carrierStatus.edit && value.carrierStatus.view,
        },
        customers: {
          view: value.customers.view,
          edit: value.customers.edit && value.customers.view,
        },
        notifications: {
          view: value.notifications.view,
          edit: value.notifications.edit && value.notifications.view,
        },
        packages: {
          view: value.packages.view,
          edit: value.packages.edit && value.packages.view,
        },
        systemUsers: {
          view: value.systemUsers.view,
          edit: value.systemUsers.edit && value.systemUsers.view,
        },
        appSettings: {
          view: value.appSettings.view,
          edit: value.appSettings.edit && value.appSettings.view,
        },
        promocodes: {
          view: value.promocodes.view,
          edit: value.promocodes.edit && value.promocodes.view,
        },
        promoCampaigns: {
          view: value.promoCampaigns.view,
          edit: value.promoCampaigns.edit && value.promoCampaigns.view,
        },
      };
    } else {
      return value;
    }
  }, []);

  return (
    <ItemContent
      itemId={id}
      title={t(tPaths.itemContent.editSystemUser)}
      path={EPaths.SystemUsers}
      contentMapper={dataMapper}
      updateDataFormatter={updateDataFormatter}
      switches={switches}
      switchesDataMapper={switchesDataMapper}
      editable={permissions.systemUsers.edit}
      onChangeFormatter={handleOnChange}
    />
  );
};

export default EditSystemUser;
